'use client'

import { Button } from '@/components/ui/button'
import { Home, RotateCw } from 'lucide-react'
import Link from 'next/link'

/* A handful of deterministic "stars" so the field is stable across renders. */
const stars = [
  { top: '14%', left: '10%', size: 2, delay: '0s' },
  { top: '20%', left: '80%', size: 3, delay: '0.6s' },
  { top: '36%', left: '26%', size: 2, delay: '1.2s' },
  { top: '16%', left: '56%', size: 2, delay: '0.3s' },
  { top: '60%', left: '16%', size: 3, delay: '0.9s' },
  { top: '66%', left: '78%', size: 2, delay: '1.5s' },
  { top: '44%', left: '90%', size: 2, delay: '0.4s' },
  { top: '80%', left: '44%', size: 3, delay: '1.1s' },
  { top: '30%', left: '70%', size: 2, delay: '1.8s' },
  { top: '84%', left: '62%', size: 2, delay: '0.7s' },
  { top: '10%', left: '40%', size: 2, delay: '1.4s' },
  { top: '54%', left: '50%', size: 2, delay: '0.2s' },
]

/*
 * The screen we show for any unhandled client-side exception. Shared by both
 * app/error.tsx (segment errors) and app/global-error.tsx (root-layout errors),
 * so it must not depend on any React context/provider — global-error renders
 * outside them. Everything here is pure CSS/SVG + brand vars, no external assets.
 */
export function ErrorScreen({ reset }: { reset?: () => void }) {
  return (
    <div className='relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground antialiased'>
      {/* Scoped keyframes so this works even when Tailwind's config hasn't
          loaded (e.g. inside global-error, which replaces the root layout). */}
      <style>{robotStyles}</style>

      {/* Ambient background — mirrors the landing hero / 404 page */}
      <div className='pointer-events-none absolute inset-0 -z-10'>
        <div className='absolute inset-0 bg-gradient-to-b from-background via-background to-muted/40' />
        <div
          className='absolute -top-40 left-1/2 h-[34rem] w-[64rem] -translate-x-1/2 rounded-full opacity-50 blur-3xl'
          style={{
            background:
              'radial-gradient(closest-side, hsl(var(--destructive) / 0.22), transparent)',
          }}
        />
        <div
          className='absolute bottom-[-12rem] right-[-8rem] h-[28rem] w-[28rem] rounded-full opacity-40 blur-3xl'
          style={{
            background:
              'radial-gradient(closest-side, hsl(var(--accent) / 0.22), transparent)',
          }}
        />
        <div
          className='absolute inset-0 opacity-[0.18]'
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage:
              'radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 100%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 100%)',
          }}
        />
        {/* Starfield */}
        {stars.map((s, i) => (
          <span
            key={i}
            className='es-twinkle absolute rounded-full bg-accent/70'
            style={{
              top: s.top,
              left: s.left,
              height: s.size,
              width: s.size,
              animationDelay: s.delay,
              boxShadow: '0 0 6px hsl(var(--accent) / 0.6)',
            }}
          />
        ))}
      </div>

      {/* Main */}
      <main className='flex flex-1 items-center justify-center px-4 py-12 sm:px-6'>
        <div className='mx-auto flex max-w-xl flex-col items-center text-center'>
          <div className='es-fade-up'>
            <BrokenRobot />
          </div>

          <div className='es-fade-up mt-8' style={{ animationDelay: '140ms' }}>
            <div className='inline-flex items-center gap-2 rounded-full border border-border/70 bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur'>
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive/70' />
                <span className='relative inline-flex h-2 w-2 rounded-full bg-destructive' />
              </span>
              Unexpected error
            </div>

            <h1 className='mt-6 text-3xl font-bold tracking-tight sm:text-4xl'>
              Sorry about that — there seems to be an error
            </h1>

            <p className='mx-auto mt-4 max-w-md text-balance text-base leading-relaxed text-muted-foreground'>
              Something went sideways on our end. Your work is safe. Try again,
              or head back home and pick up where you left off.
            </p>

            <div className='mt-8 flex flex-wrap items-center justify-center gap-3'>
              <Button size='lg' asChild className='rounded-full px-6 text-base'>
                <Link href='/'>
                  <Home className='mr-2 h-4 w-4' />
                  Go home
                </Link>
              </Button>
              {reset && (
                <Button
                  size='lg'
                  variant='outline'
                  onClick={reset}
                  className='rounded-full px-6 text-base'
                >
                  <RotateCw className='mr-2 h-4 w-4' />
                  Try again
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

/* A pure-SVG robot with a cracked visor, drooping sparking antenna and glitchy
   eyes. Bobs gently while sparks and a warning flicker keep it lively. */
function BrokenRobot() {
  return (
    <div className='relative mx-auto aspect-square w-56 sm:w-64'>
      {/* Soft glow behind the bot */}
      <div
        className='absolute inset-6 rounded-full opacity-70 blur-2xl'
        style={{
          background:
            'radial-gradient(closest-side, hsl(var(--destructive) / 0.20), transparent)',
        }}
      />

      <div className='es-bob absolute inset-0 flex items-center justify-center'>
        <svg
          width='224'
          height='224'
          viewBox='0 0 224 224'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className='drop-shadow-2xl'
        >
          {/* drooping, broken antenna */}
          <g className='es-antenna' style={{ transformOrigin: '112px 46px' }}>
            <line
              x1='112'
              y1='46'
              x2='138'
              y2='24'
              stroke='hsl(var(--muted-foreground))'
              strokeWidth='4'
              strokeLinecap='round'
            />
            <circle cx='140' cy='22' r='7' fill='hsl(var(--destructive))'>
              <animate
                attributeName='opacity'
                values='1;0.3;1;0.5;1'
                dur='0.9s'
                repeatCount='indefinite'
              />
            </circle>
            {/* sparks */}
            <g className='es-spark'>
              <path
                d='M140 22 l6 -8 M140 22 l9 2 M140 22 l3 9'
                stroke='hsl(var(--accent))'
                strokeWidth='2.5'
                strokeLinecap='round'
              />
            </g>
          </g>

          {/* neck */}
          <rect x='104' y='42' width='16' height='14' rx='4' fill='hsl(var(--muted-foreground))' opacity='0.5' />

          {/* head shell */}
          <rect x='52' y='50' width='120' height='96' rx='26' fill='#e9eaee' />
          <rect x='52' y='50' width='120' height='96' rx='26' fill='hsl(var(--border))' opacity='0.25' />
          {/* ears */}
          <rect x='40' y='84' width='14' height='30' rx='6' fill='hsl(var(--muted-foreground))' opacity='0.5' />
          <rect x='170' y='84' width='14' height='30' rx='6' fill='hsl(var(--muted-foreground))' opacity='0.5' />

          {/* dark visor / face screen */}
          <rect x='66' y='66' width='92' height='64' rx='16' fill='hsl(var(--foreground))' />
          <rect x='66' y='66' width='92' height='64' rx='16' fill='hsl(var(--destructive))' opacity='0.08'>
            <animate
              attributeName='opacity'
              values='0.02;0.16;0.04;0.12;0.02'
              dur='2.4s'
              repeatCount='indefinite'
            />
          </rect>

          {/* glitchy X-eyes */}
          <g className='es-eyes' stroke='hsl(var(--accent))' strokeWidth='4.5' strokeLinecap='round'>
            <path d='M84 88 l14 14 M98 88 l-14 14' />
            <path d='M126 88 l14 14 M140 88 l-14 14' />
          </g>
          {/* flat wavy mouth */}
          <path
            d='M88 116 q8 -7 16 0 t16 0 t16 0'
            stroke='hsl(var(--accent))'
            strokeWidth='4'
            strokeLinecap='round'
            fill='none'
            opacity='0.85'
          />

          {/* crack across the visor */}
          <path
            d='M120 66 l-10 22 l12 6 l-8 24'
            stroke='hsl(var(--background))'
            strokeWidth='2.5'
            strokeLinejoin='round'
            fill='none'
            opacity='0.9'
          />

          {/* body */}
          <rect x='72' y='150' width='80' height='40' rx='14' fill='#e9eaee' />
          <rect x='72' y='150' width='80' height='40' rx='14' fill='hsl(var(--border))' opacity='0.25' />
          <rect x='92' y='162' width='40' height='10' rx='5' fill='hsl(var(--destructive))' opacity='0.6' />
          <circle cx='150' cy='157' r='3' fill='hsl(var(--muted-foreground))' opacity='0.6' />
        </svg>
      </div>

      {/* loose bolt tumbling out */}
      <div
        className='es-bolt absolute'
        style={{ left: '30%', top: '78%' }}
        aria-hidden
      >
        <div className='h-3 w-3 rounded-[3px] border-2 border-muted-foreground/60 bg-muted' />
      </div>
    </div>
  )
}

/* Self-contained keyframes (prefixed es-* to avoid collisions). Kept inline so
   the animations survive in global-error, which renders without the app shell. */
const robotStyles = `
@keyframes es-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes es-bob { 0%, 100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-10px) rotate(1deg); } }
@keyframes es-antenna { 0%, 100% { transform: rotate(0deg); } 50% { transform: rotate(6deg); } }
@keyframes es-spark { 0%, 60%, 100% { opacity: 0; } 65%, 80% { opacity: 1; } }
@keyframes es-eyes { 0%, 92%, 100% { opacity: 1; transform: translateX(0); } 94% { opacity: 0.4; transform: translateX(1.5px); } 96% { opacity: 1; transform: translateX(-1.5px); } }
@keyframes es-twinkle { 0%, 100% { opacity: 0.25; } 50% { opacity: 1; } }
@keyframes es-bolt { 0% { opacity: 0; transform: translateY(-6px) rotate(0deg); } 15% { opacity: 1; } 100% { opacity: 0; transform: translateY(46px) rotate(220deg); } }
.es-fade-up { opacity: 0; animation: es-fade-up 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.es-bob { animation: es-bob 5s ease-in-out infinite; }
.es-antenna { animation: es-antenna 2.6s ease-in-out infinite; }
.es-spark { animation: es-spark 1.8s ease-in-out infinite; }
.es-eyes { animation: es-eyes 3.2s steps(1, end) infinite; }
.es-twinkle { animation: es-twinkle 2.4s ease-in-out infinite; }
.es-bolt { animation: es-bolt 2.8s ease-in 0.6s infinite; }
@media (prefers-reduced-motion: reduce) {
  .es-fade-up { animation: none; opacity: 1; }
  .es-bob, .es-antenna, .es-spark, .es-eyes, .es-twinkle, .es-bolt { animation: none; }
}
`
